import { useEffect, useLayoutEffect, useRef } from 'react'
import { LuSearch, LuX } from 'react-icons/lu'

import { useTagToggle } from '../../hooks/useTagToggle'
import { useTagsStore } from '../../store'
import { AssetTag } from '../TableHeaderTags/TableHeaderTags.types'
import { TagItem } from './TagItem'
import styles from './TagsPopout.module.css'
import { TagsListViewProps } from './TagsPopout.types'

export const TagsListView: React.FC<TagsListViewProps> = ({
  searchTerm,
  onSearchChange,
  searchFilteredTags,
  isTagsUpdating,
  onCreateNewTag,
  onEditTag,
  onClose,
}) => {
  const tagsListRef = useRef<HTMLDivElement>(null)
  const scrollPositionRef = useRef<number>(0)
  const shouldRestoreScrollRef = useRef<boolean>(false)
  const { handleTagToggle } = useTagToggle()
  const { activeAssetData } = useTagsStore()

  // Only restore scroll position when we've just toggled a tag
  useLayoutEffect(() => {
    if (
      shouldRestoreScrollRef.current &&
      tagsListRef.current &&
      scrollPositionRef.current > 0 &&
      !searchTerm.trim()
    ) {
      tagsListRef.current.scrollTop = scrollPositionRef.current
      shouldRestoreScrollRef.current = false
    }
  })

  // Reset scroll position when searching
  useEffect(() => {
    if (searchTerm.trim() && tagsListRef.current) {
      scrollPositionRef.current = 0
      tagsListRef.current.scrollTop = 0
      shouldRestoreScrollRef.current = false
    }
  }, [searchTerm])

  const onTagToggle = (_tagId: number, isSelected: boolean, tag: AssetTag) => {
    // Store current scroll position before toggling
    if (tagsListRef.current) {
      scrollPositionRef.current = tagsListRef.current.scrollTop
      shouldRestoreScrollRef.current = true
    }

    handleTagToggle(tag, isSelected, (error) => {
      // For now, just log the error. In the future, we could show a toast or inline error
      console.error('Tag toggle error:', error)
    })
  }

  return (
    <>
      <div className={styles.popoutHeader}>
        <div className={styles.searchContainer}>
          <LuSearch size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.headerActions}>
          {/* disable for now since popout is first priority */}
          {/* <button className={styles.actionButton} onClick={onOpenModal}>
            <LuMaximize2 size={16} />
          </button> */}
          <button
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          >
            <LuX size={16} />
          </button>
        </div>
      </div>

      <div ref={tagsListRef} className={styles.tagsList}>
        <p>
          {activeAssetData?.tags?.length === 10 ? (
            <p>The maximum number of assignable tags has been reached for this asset.</p>
          ) : (
            ''
          )}
        </p>
        {isTagsUpdating && searchFilteredTags.length === 0 ? (
          <div className={styles.loadingState}>Loading tags...</div>
        ) : searchFilteredTags.length === 0 ? (
          <div className={styles.emptyState}>
            {searchTerm ? 'No tags match your search' : 'No tags available'}
          </div>
        ) : (
          searchFilteredTags.map((tag: AssetTag) => {
            // Get the latest state from activeAssetData instead of currentAssetTags
            const isSelected = activeAssetData?.tags?.some((t) => t.id === tag.id) || false
            // Disable unselected tags when asset already has 10 tags
            const hasMaxTags = (activeAssetData?.tags?.length || 0) >= 10
            const isDisabled = !isSelected && hasMaxTags

            return (
              <TagItem
                key={`tag-item-${tag.id}`}
                tag={tag}
                isSelected={isSelected}
                isTagsUpdating={isTagsUpdating}
                disabled={isDisabled}
                onToggle={onTagToggle}
                onEdit={onEditTag}
              />
            )
          })
        )}
      </div>

      <button
        className={styles.createNewButton}
        onClick={(e) => {
          e.stopPropagation()
          onCreateNewTag()
        }}
      >
        Create New Tag
      </button>
    </>
  )
}
